import * as expressionNode from '../../../../node'
import type { Fn } from '../../../fn'
import type { FnHelper } from '../../../helper'
import { removeIsolatedValueNodes } from '../remove-isolated-value-nodes'

import { usingExpressionList } from './using-expression-list'

/**
 * Used for standard comparisons: `>`, `<`, `>=`, `<=`
 */
export function comparison<TFn extends Fn>({
  compare,
  getArgExpressions,
  nodeClass,
}: comparison.Params<TFn>): FnHelper<TFn>['createNode'] {
  return usingExpressionList({
    // If there are no items, then it is reasonable to say each item is
    // greater than or equal to the next
    emptyValue: true,
    finalizeArgs: args => removeIsolatedValueNodes(args),
    getArgExpressions,
    handleValue: (node, stash) => {
      if (!compare(Number(stash.previousValue), Number(node.value))) {
        return { return: false }
      }
      stash.previousValue = node.value
      // We still need this node to use in comparing to non-value nodes
      return new expressionNode.ForValue(node.value)
    },
    nodeClass,
    setupStash: (): { previousValue: unknown } => ({
      previousValue: Infinity,
    }),
    // GreaterThanOrEqualTo is the same with 0 or 1 arguments
    treatSingleArgAs: 'empty',
  })
}
export namespace comparison {
  export interface Params<TFn extends Fn>
    extends Pick<
      usingExpressionList.Params<TFn, readonly expressionNode.Node[]>,
      'nodeClass' | 'getArgExpressions'
    > {
    compare: (a: number, b: number) => boolean
  }
}
